// EmployeDashboard.jsx – version avec cache Dexie 24h et design amélioré
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line,
  Area, AreaChart
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faUserCheck, faUserTimes, faVenusMars,
  faBriefcase, faMoneyBillWave, faChartLine, faCalendarAlt,
  faGraduationCap, faBuilding, faSpinner, faSyncAlt,
  faExclamationTriangle, faChartPie, faUserGraduate,
  faClock, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_employe_stats.php`;

// ---------- Initialisation Dexie ----------
const db = new Dexie('EmployeDashboardCache');
db.version(1).stores({
  stats: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures

async function getCachedStats(key) {
  try {
    const entry = await db.stats.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedStats(key, data) {
  try {
    await db.stats.put({ key, data, timestamp: Date.now() });
  } catch (e) { console.warn('Cache set error', e); }
}

// Palette de couleurs
const COLORS = {
  'M': '#3b82f6',
  'F': '#ec4899',
  'CDI': '#22c55e',
  'CDD': '#f59e0b',
  'Stage': '#8b5cf6',
  'Apprentissage': '#06b6d4',
  'Consultant': '#f472b6',
  'actif': '#22c55e',
  'inactif': '#ef4444'
};

const CHART_COLORS = ['#3b82f6', '#ec4899', '#22c55e', '#f59e0b', '#8b5cf6', '#06b6d4', '#f472b6', '#14b8a6'];

const EmployeDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');

  const loadData = useCallback(async (forceRefresh = false) => {
    const cacheKey = 'dashboard_stats';
    setError(null);
    if (!forceRefresh) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      // 1. Cache
      if (!forceRefresh) {
        const cached = await getCachedStats(cacheKey);
        if (cached) {
          setData(cached);
          setSource('📦 Cache (24h)');
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      // 2. Réseau
      const url = `${API_URL}?action=dashboard_stats&_=${Date.now()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status === 'success') {
        setData(json.data);
        setSource('🔄 Live');
        await setCachedStats(cacheKey, json.data);
      } else {
        throw new Error(json.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message);
      // En cas d'erreur, tentative de récupérer un cache périmé
      try {
        const stale = await db.stats.get(cacheKey);
        if (stale) {
          setData(stale.data);
          setSource('⚠️ Cache périmé');
          setError(null);
        }
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && !data) {
    return (
      <div className="emp-dashboard-loading" style={{ textAlign: 'center', padding: '3rem', background: 'rgba(10,14,39,0.7)', borderRadius: '24px', marginTop: '2rem' }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#3b82f6" />
        <p style={{ marginTop: '1rem', color: '#94a3b8' }}>Chargement du tableau de bord...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="emp-dashboard-error" style={{ textAlign: 'center', padding: '2rem', background: 'rgba(10,14,39,0.7)', borderRadius: '24px', marginTop: '2rem', color: '#ef4444' }}>
        <FontAwesomeIcon icon={faExclamationTriangle} /> Erreur : {error}
        <br />
        <button onClick={() => loadData(true)} style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e7ff', padding: '0.5rem 1.5rem', borderRadius: '30px', cursor: 'pointer' }}>
          <FontAwesomeIcon icon={faSyncAlt} /> Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  const {
    total_employes,
    actif_inactif,
    sexe,
    contrats,
    services,
    salaire_moyen,
    evolution_embauches,
    departements,
    age_moyen,
    niveau_etude
  } = data;

  const sexeData = Object.keys(sexe).map(key => ({ name: key === 'M' ? 'Homme' : 'Femme', value: sexe[key] }));
  const contratData = Object.keys(contrats).map(key => ({ name: key, value: contrats[key] }));
  const serviceData = Object.keys(services).map(key => ({ name: key, value: services[key] }));
  const deptData = Object.keys(departements).map(key => ({ name: key, value: departements[key] }));
  const niveauData = Object.keys(niveau_etude).map(key => ({ name: key, value: niveau_etude[key] }));

  const totalActifs = actif_inactif.actif || 0;
  const totalInactifs = actif_inactif.inactif || 0;
  const tauxActif = total_employes > 0 ? Math.round((totalActifs / total_employes) * 100) : 0;

  return (
    <div className="employe-dashboard">
      <style jsx>{`
        /* ---------- Styles améliorés ---------- */
        .employe-dashboard {
          padding: clamp(0.8rem, 2vw, 1.8rem);
          background: linear-gradient(145deg, rgba(10,14,39,0.92), rgba(20,30,60,0.85));
          backdrop-filter: blur(16px);
          border-radius: 28px;
          border: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 16px 48px rgba(0,0,0,0.5);
          color: #e0e7ff;
          margin-top: 2rem;
          font-family: 'Inter', -apple-system, sans-serif;
          transition: all 0.3s ease;
        }
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.2rem;
        }
        .dashboard-header h2 {
          margin: 0;
          font-size: clamp(1.2rem, 3.5vw, 1.8rem);
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          background: linear-gradient(to right, #e0e7ff, #93c5fd);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .dashboard-header h2 svg {
          -webkit-text-fill-color: #3b82f6;
        }
        .header-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .source-badge {
          font-size: clamp(0.5rem, 1.2vw, 0.7rem);
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          background: rgba(59,130,246,0.12);
          color: #93c5fd;
          border: 1px solid rgba(59,130,246,0.1);
          white-space: nowrap;
        }
        .refresh-btn {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          color: #e0e7ff;
          padding: 0.3rem 0.8rem;
          border-radius: 30px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: clamp(0.65rem, 1.5vw, 0.8rem);
          transition: 0.25s;
          min-height: 34px;
        }
        .refresh-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.1);
          transform: scale(1.02);
        }
        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: 0.8rem;
          margin-bottom: 1.5rem;
        }
        .kpi-card {
          background: rgba(255,255,255,0.03);
          border-radius: 18px;
          padding: 0.7rem 0.5rem;
          border: 1px solid rgba(255,255,255,0.04);
          text-align: center;
          transition: 0.3s;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .kpi-card:hover {
          background: rgba(255,255,255,0.06);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }
        .kpi-label {
          font-size: clamp(0.5rem, 1.4vw, 0.7rem);
          text-transform: uppercase;
          color: #94a3b8;
          letter-spacing: 0.04em;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.3rem;
          font-weight: 500;
        }
        .kpi-value {
          font-size: clamp(1.1rem, 3.5vw, 1.8rem);
          font-weight: 800;
          margin-top: 0.1rem;
          color: #f8fafc;
          line-height: 1.2;
        }
        .kpi-sub {
          font-size: clamp(0.45rem, 1vw, 0.6rem);
          color: #94a3b8;
          margin-top: 0.1rem;
        }

        .progress-section {
          background: rgba(255,255,255,0.03);
          border-radius: 20px;
          padding: 0.8rem 1rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .progress-title {
          font-weight: 600;
          font-size: clamp(0.8rem, 2vw, 1rem);
          margin-bottom: 0.3rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
        }
        .progress-bar-track {
          height: 24px;
          background: rgba(255,255,255,0.05);
          border-radius: 40px;
          overflow: hidden;
          display: flex;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
        }
        .progress-segment {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(0.55rem, 1.2vw, 0.7rem);
          font-weight: 700;
          color: rgba(0,0,0,0.7);
          transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
          text-shadow: 0 1px 2px rgba(255,255,255,0.2);
        }
        .progress-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem 1.2rem;
          font-size: clamp(0.55rem, 1.4vw, 0.7rem);
          margin-top: 0.4rem;
          color: #cbd5e1;
        }
        .legend-dot {
          width: 12px;
          height: 12px;
          border-radius: 4px;
          display: inline-block;
          margin-right: 0.2rem;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.8rem;
        }
        .chart-card {
          background: rgba(255,255,255,0.02);
          border-radius: 18px;
          padding: 0.6rem;
          border: 1px solid rgba(255,255,255,0.04);
          transition: 0.3s;
        }
        .chart-card:hover {
          border-color: rgba(59,130,246,0.15);
        }
        .chart-title {
          font-size: clamp(0.6rem, 1.8vw, 0.8rem);
          font-weight: 500;
          text-align: center;
          color: #cbd5e1;
          margin-bottom: 0.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.3rem;
        }
        .chart-container {
          height: 160px;
        }
        .full-width {
          grid-column: 1 / -1;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }
          .kpi-card {
            padding: 0.4rem 0.3rem;
          }
          .progress-bar-track {
            height: 18px;
          }
        }
        @media (max-width: 420px) {
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.3rem;
          }
          .kpi-card {
            padding: 0.3rem 0.2rem;
            border-radius: 12px;
          }
        }
        .text-secondary { color: #94a3b8; }
        .text-success { color: #22c55e; }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .mb-1 { margin-bottom: 0.3rem; }
        .gap-1 { gap: 0.3rem; }
        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .flex-wrap { flex-wrap: wrap; }
        .justify-content-between { justify-content: space-between; }
      `}</style>

      <div className="dashboard-header">
        <h2><FontAwesomeIcon icon={faUsers} /> Employés</h2>
        <div className="header-actions">
          <span className="source-badge">{source || '📦'}</span>
          <button className="refresh-btn" onClick={() => loadData(true)} disabled={refreshing}>
            {refreshing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSyncAlt} />}
            {refreshing ? '...' : 'Forcer'}
          </button>
          <button className="refresh-btn" onClick={() => loadData(false)} disabled={loading || refreshing}>
            <FontAwesomeIcon icon={faSyncAlt} /> Rafraîchir
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faUsers} /> Total</div>
          <div className="kpi-value">{total_employes}</div>
          <div className="kpi-sub">employés</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faUserCheck} /> Actifs</div>
          <div className="kpi-value" style={{ color: '#22c55e' }}>{totalActifs}</div>
          <div className="kpi-sub">{tauxActif}% du total</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faUserTimes} /> Inactifs</div>
          <div className="kpi-value" style={{ color: '#ef4444' }}>{totalInactifs}</div>
          <div className="kpi-sub">{100 - tauxActif}%</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faMoneyBillWave} /> Salaire moyen</div>
          <div className="kpi-value">{salaire_moyen.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</div>
          <div className="kpi-sub">USD</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faCalendarAlt} /> Âge moyen</div>
          <div className="kpi-value">{age_moyen}</div>
          <div className="kpi-sub">ans</div>
        </div>
      </div>

      {/* Barre de progression actif/inactif */}
      <div className="progress-section">
        <div className="progress-title">
          <span><FontAwesomeIcon icon={faShieldAlt} /> Actifs vs Inactifs</span>
          <span>{tauxActif}% actifs</span>
        </div>
        <div className="progress-bar-track">
          <div className="progress-segment" style={{ width: `${tauxActif}%`, background: 'linear-gradient(90deg, #22c55e, #4ade80)' }}>
            {tauxActif > 12 && totalActifs}
          </div>
          <div className="progress-segment" style={{ width: `${100 - tauxActif}%`, background: 'linear-gradient(90deg, #ef4444, #f87171)' }}>
            {100 - tauxActif > 12 && totalInactifs}
          </div>
        </div>
        <div className="progress-legend">
          <span><span className="legend-dot" style={{ background: '#22c55e' }}></span> Actifs ({totalActifs})</span>
          <span><span className="legend-dot" style={{ background: '#ef4444' }}></span> Inactifs ({totalInactifs})</span>
        </div>
      </div>

      {/* Graphiques */}
      <div className="charts-grid">
        {/* Sexe */}
        <div className="chart-card">
          <div className="chart-title"><FontAwesomeIcon icon={faVenusMars} /> Sexe</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sexeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={55}
                  innerRadius={28}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize="0.6rem"
                >
                  {sexeData.map((entry, idx) => (
                    <Cell key={entry.name} fill={entry.name === 'Homme' ? COLORS.M : COLORS.F} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: '0.65rem' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Contrats */}
        <div className="chart-card">
          <div className="chart-title"><FontAwesomeIcon icon={faBriefcase} /> Contrats</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={contratData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 8 }} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 8 }} width={35} />
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: '0.65rem' }} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Services */}
        <div className="chart-card">
          <div className="chart-title"><FontAwesomeIcon icon={faBuilding} /> Services (top 5)</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 8 }} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 8 }} width={35} />
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: '0.65rem' }} />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Niveau d'étude */}
        <div className="chart-card">
          <div className="chart-title"><FontAwesomeIcon icon={faGraduationCap} /> Niveau d'étude</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={niveauData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 8 }} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 8 }} width={35} />
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: '0.65rem' }} />
                <Bar dataKey="value" fill="#f59e0b" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Évolution des embauches */}
        <div className="chart-card full-width">
          <div className="chart-title"><FontAwesomeIcon icon={faChartLine} /> Évolution des embauches (12 mois)</div>
          <div className="chart-container" style={{ height: '160px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={evolution_embauches} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="mois" stroke="#94a3b8" tick={{ fontSize: 8 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 8 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: '0.65rem' }} />
                <Area type="monotone" dataKey="nb" stroke="#3b82f6" fill="url(#colorGrad)" strokeWidth={2} />
                <defs>
                  <linearGradient id="colorGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeDashboard;